// src/pages/analyst/SquadInterests.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2,
  Users,
  Building2,
  Mail,
  Calendar,
  CheckCircle2,
  XCircle,
  Eye,
  MessageSquare,
  Clock,
  Sparkles,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface SquadInterest {
  id: string;
  problem_id: string;
  squad_id: string;
  squad_member_id: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  expressed_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  analyst_notes: string | null;
  problem?: {
    id: string;
    title: string;
    problem_id: string;
    industry_sector: string;
    quantification: any;
  };
  squad?: {
    id: string;
    squad_name: string;
    university: string;
    squad_code: string;
  };
  squad_member?: {
    id: string;
    full_name: string;
    email: string;
    role: string;
  };
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle }
};

function formatCurrency(value: number): string {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
}

export default function SquadInterests() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [interests, setInterests] = useState<SquadInterest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInterest, setSelectedInterest] = useState<SquadInterest | null>(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected'>('approved');
  const [analystNotes, setAnalystNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');

  useEffect(() => {
    loadInterests();
  }, []);

  const loadInterests = async () => {
    setIsLoading(true);
    try {
      // Fetch all squad interests with related data
      const { data, error } = await supabase
        .from('squad_problem_interests')
        .select(`
          *,
          problem:problem_id (
            id,
            title,
            problem_id,
            industry_sector,
            quantification
          ),
          squad:squad_id (
            id,
            squad_name,
            university,
            squad_code
          ),
          squad_member:squad_member_id (
            id,
            full_name,
            email,
            role
          )
        `)
        .order('expressed_at', { ascending: false });

      if (error) throw error;
      
      setInterests(data || []);
      console.log('📊 Loaded interests:', data?.length);
      
    } catch (error) {
      console.error('Error loading interests:', error);
      toast({
        title: 'Error',
        description: 'Failed to load squad interests',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReview = (interest: SquadInterest) => {
    setSelectedInterest(interest);
    setAnalystNotes('');
    setShowReviewDialog(true);
  };

  const submitReview = async () => {
    if (!selectedInterest || !user) return;
    
    setIsSubmitting(true);
    try {
      // Update the interest status
      const { error: updateError } = await supabase
        .from('squad_problem_interests')
        .update({
          status: reviewDecision,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
          analyst_notes: analystNotes || null
        })
        .eq('id', selectedInterest.id);

      if (updateError) throw updateError;

      // If approved, also assign the problem to the squad
      if (reviewDecision === 'approved') {
        // Get the problem to update its status
        const { error: problemError } = await supabase
          .from('problem_bank')
          .update({
            status: 'assigned',
            assigned_to_squad_id: selectedInterest.squad_id,
            assigned_to_squad_name: selectedInterest.squad?.squad_name,
            assigned_at: new Date().toISOString(),
            assigned_by: user.id
          })
          .eq('id', selectedInterest.problem_id);

        if (problemError) {
          console.error('Error updating problem:', problemError);
          toast({
            title: 'Partial Success',
            description: 'Interest approved but problem assignment failed. Please assign manually.',
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Interest Approved!',
            description: `Problem has been assigned to ${selectedInterest.squad?.squad_name}.`,
          });
        }
      } else {
        toast({
          title: 'Interest Rejected',
          description: `The interest request has been rejected.`,
        });
      }

      setShowReviewDialog(false);
      setSelectedInterest(null);
      setAnalystNotes('');
      
      // Reload interests
      await loadInterests();
      
    } catch (error) {
      console.error('Error reviewing interest:', error);
      toast({
        title: 'Error',
        description: 'Failed to process review. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPendingCount = () => {
    return interests.filter(i => i.status === 'pending').length;
  };

  const filteredInterests = interests.filter(i => i.status === activeTab);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Squad Interests</h1>
          <p className="text-muted-foreground">
            Review and manage squad requests to work on problems
          </p>
        </div>
        <Button variant="outline" onClick={loadInterests} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-3xl font-bold text-yellow-600">{getPendingCount()}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-3xl font-bold text-green-600">
                  {interests.filter(i => i.status === 'approved').length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejected</p>
                <p className="text-3xl font-bold text-red-600">
                  {interests.filter(i => i.status === 'rejected').length}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending" value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList>
          <TabsTrigger value="pending">
            Pending
            {getPendingCount() > 0 && (
              <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-700">
                {getPendingCount()}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4 mt-4">
          {filteredInterests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No {activeTab} interests found</p>
              </CardContent>
            </Card>
          ) : (
            filteredInterests.map((interest) => {
              const StatusIcon = STATUS_CONFIG[interest.status].icon;
              const annualLoss = interest.problem?.quantification?.totalLossPerYear || 0;
              
              return (
                <Card key={interest.id} className="overflow-hidden">
                  <CardHeader className="pb-3 bg-muted/30">
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div>
                        <CardTitle className="text-lg">
                          {interest.problem?.title || 'Unknown Problem'}
                        </CardTitle>
                        <CardDescription className="text-xs mt-1">
                          {interest.problem?.problem_id} • {interest.problem?.industry_sector?.replace('_', ' ') || 'N/A'}
                        </CardDescription>
                      </div>
                      <Badge className={STATUS_CONFIG[interest.status].color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {STATUS_CONFIG[interest.status].label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Squad Information */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Squad Information
                        </h3>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="font-medium">{interest.squad?.squad_name || 'Unknown Squad'}</p>
                          <p className="text-sm text-muted-foreground">{interest.squad?.university || 'N/A'}</p>
                          <p className="text-xs text-muted-foreground mt-1">Code: {interest.squad?.squad_code || 'N/A'}</p>
                        </div>
                        
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-sm font-medium">Expressed By</p>
                          <p className="text-sm">{interest.squad_member?.full_name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Mail className="h-3 w-3" />
                            {interest.squad_member?.email || 'N/A'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Role: {interest.squad_member?.role?.replace('_', ' ') || 'Team Member'}
                          </p>
                        </div>
                      </div>

                      {/* Interest Details */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Interest Details
                        </h3>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-sm font-medium">Reason</p>
                          <p className="text-sm text-muted-foreground mt-1">{interest.reason}</p>
                        </div>
                        
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Expressed:</span>
                          <span>{new Date(interest.expressed_at).toLocaleDateString()}</span>
                        </div>
                        
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Annual Loss:</span>
                          <span className="font-medium text-red-600">{formatCurrency(annualLoss)}</span>
                        </div>

                        {interest.analyst_notes && (
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-blue-700">Review Notes</p>
                            <p className="text-sm text-blue-600 mt-1">{interest.analyst_notes}</p>
                          </div>
                        )}

                        {interest.status === 'pending' && (
                          <div className="flex gap-2 mt-4">
                            <Button
                              onClick={() => {
                                setReviewDecision('approved');
                                handleReview(interest);
                              }}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => {
                                setReviewDecision('rejected');
                                handleReview(interest);
                              }}
                              variant="destructive"
                              className="flex-1"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => navigate(`/problem-bank/problem/${interest.problem_id}`)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Problem Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reviewDecision === 'approved' ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Approve Interest Request
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-600" />
                  Reject Interest Request
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {reviewDecision === 'approved' 
                ? 'This will assign the problem to the squad and notify them.'
                : 'This will reject the squad\'s interest and notify them.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/30 p-3 rounded-lg">
              <p className="text-sm font-medium">Problem:</p>
              <p className="text-sm">{selectedInterest?.problem?.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Squad: {selectedInterest?.squad?.squad_name}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="analystNotes">Analyst Notes (Optional)</Label>
              <Textarea
                id="analystNotes"
                placeholder={reviewDecision === 'approved' 
                  ? "Add any instructions or expectations for the squad..."
                  : "Explain why this request was rejected..."}
                value={analystNotes}
                onChange={(e) => setAnalystNotes(e.target.value)}
                rows={3}
              />
            </div>

            {reviewDecision === 'approved' && (
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-xs text-green-700 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Approving will automatically assign this problem to the squad and start their 90-day journey.
                </p>
              </div>
            )}

            {reviewDecision === 'rejected' && (
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-xs text-red-700 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Rejection will notify the squad that their request was not approved.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitReview} 
              disabled={isSubmitting}
              className={reviewDecision === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {reviewDecision === 'approved' ? 'Approve & Assign' : 'Reject Request'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}